// The wheel — SVG, built from the owner's zoomed reference crop.
//
// Reading outward-in: a dark chocolate cabinet; a wide caramel wooden bowl that
// IS the ball track; eight brass diamond fittings set into the wood where the
// ball strikes them; a gold ring; the number band (coloured pocket + white
// numeral); a second gold ring; an inner apron of shorter coloured bars; a
// burled-walnut cone with a soft diagonal sweep; a small brass hub.
//
// ── RULE ZERO ────────────────────────────────────────────────────────────
// There is not one `filter` attribute in this file, and there must never be.
// The rotor takes a fresh transform every animation frame, and both Blink and
// WebKit re-run an entire filter graph into a new buffer on every transform
// change — a 400x400 region at DPR 3 is 1.44M pixels per primitive per frame.
// Banned outright: feTurbulence, feDisplacementMap, feMorphology,
// feConvolveMatrix, feDiffuseLighting, feSpecularLighting (the "realistic wood"
// recipe, and the worst of them on phones) and feImage. Wood, brass and motion
// blur are ARTWORK — gradients and shapes — which costs nothing per frame.
//
// ── ONE LIGHT SOURCE ─────────────────────────────────────────────────────
// Every gradient is userSpaceOnUse and lit from (LIGHT_X, LIGHT_Y). An earlier
// build gave each ring its own objectBoundingBox gradient with its own
// highlight, which is exactly why it read as a stack of flat discs rather than
// one lit object.
import { useEffect, useRef } from 'react';
import {
  R_OUTER, R_RIM_IN, R_FITTING, R_GOLD, R_BAND_OUT, R_NUM, R_BAND_IN,
  R_GROOVE, R_GROOVE_W, R_RING2_OUT, R_RING2_IN, R_GOLD_IN, R_GOLD_IN_W, R_RIM_OUT,
  R_CONE, R_HUB, R_BALL, R_TRACK, R_REST,
} from './wheelGeometry';
import {
  WHEEL, POCKETS, POCKET_ARC, TAU, colourOf,
  rotorAngle, idleRotorAngle, ballAngle, ballRadius, type Spin,
} from './engine';

const VB = 400;                 // viewBox units
const CX = VB / 2, CY = VB / 2;

/* ── geometry ──────────────────────────────────────────────────────────────
 * Measured off the reference crop, normalised to the wooden bowl's outer edge,
 * See docs/ROULETTE_UI_V2_SPEC.md "WHEEL CLOSE-UP" and the measured table below.
 *
 * R_BALL is what engine.ballRadius() is given. engine.ts derives
 *     R_TRACK  = 0.985 * R      (where the ball rides)
 *     R_POCKET = 0.78  * R      (where it comes to rest)
 * That RATIO is locked by the physics; the absolute value is ours. Setting
 * R_BALL so the ball rides at 0.93 of the bowl puts its resting radius at
 * 0.736 — inside the number band, which is where a ball actually rests. The
 * apron ring below it is decoration, not a landing zone.
 */
/* The radii live in wheelGeometry.ts — ONE definition shared with the physics
 * test, because they drifted apart three times and the test kept passing
 * against geometry the wheel no longer had. See that file for the measured
 * pixel table they come from. */

const LIGHT_X = 150, LIGHT_Y = 128;

/** The inset's own viewport. It is the WHOLE wheel at 1:1 in user units — the
 *  panel's CSS box does the scaling and the clipping, so there is no zoom left
 *  in here at all.
 *
 *  MEASURED: the reference's inset is not a magnified crop. Fitting the
 *  teal/wood boundary per row (min-teal-x 77@y24, 107@y104, 77@y180) gives a
 *  circle of radius ~116 centred at (-9, 102) in panel px — a face-on slice of
 *  a wheel about HALF the size of the live one, with its hub off the panel's
 *  left edge. INSET_K = 1.75 was a 3.5x error in the wrong direction. */
export const INSET_W = 400, INSET_H = 400;

/** Where the numerals' tops point.
 *
 *  0 = OUTWARD (tops away from the hub), which is what the reference crop shows
 *  and what a real European wheel does: at the top of the wheel a number reads
 *  upright, at the bottom it reads upside down. An earlier build had 180 from a
 *  misread of the smaller reference image. */
const NUMERAL_FACING = 0;

/**
 * The shared camera for the RESULT INSET.
 *
 * The inset is a magnified crop of the SAME live wheel, drawn with
 * <use href="#rlt-wheel-root">. It is not a second <Wheel>: that would
 * duplicate ~200 nodes, add a SECOND rAF writer, and put duplicate <defs> ids
 * in one document — where url(#rlt-red) resolves document-wide to whichever
 * came first. It is not cloneNode() either, which snapshots and then freezes.
 *
 * <use> references the same live group, so the one transform written below
 * reflects in both instances: one animation writer, two paints, one set of
 * defs. Wheel's existing rAF writes this too, which is why there is still no
 * second loop.
 */
export const wheelCam: { inset: SVGGElement | null; winner: number | null; deg: number } = {
  inset: null, winner: null, deg: 0,
};

/** Write the inset's camera for a given rotor angle.
 *
 *  Shared by the rAF loop and by ResultInset's own mount, so the crop is
 *  correct on its FIRST paint rather than one frame later. Without this the
 *  inset renders an untransformed 400x400 wheel squeezed into a 118x150 box
 *  for a frame — and if rAF happens to be paused (a background tab, a device
 *  under load) it stays that way. */
export function writeInsetCam(el: SVGGElement, winner: number) {
  // No translate and no scale: the wheel fills its viewBox and the panel's CSS
  // box places it. All that is left is the swing.
  //
  // -(pocket) puts the winning pocket at 12 o'clock; +90 carries it round to
  // 3 o'clock, which is where it has to be to be VISIBLE — the hub sits off the
  // panel's left edge, so only the wheel's right side is on screen and a pocket
  // at the top would be cropped away entirely.
  //
  // WHY THERE IS NO ROTOR ANGLE IN HERE ANY MORE, and why the card looks
  // exactly the same without it: this group holds a <use> of #rlt-wheel-root,
  // and the live rotation now lives on the SVG ROOT above that group rather
  // than inside it (see the three-roots note in Wheel's return). So the <use>
  // renders the wheel at its rest orientation, where pocket n is simply
  // pocketAngleDeg(n) from the top — a constant. The old code cancelled the
  // rotor angle out (-(deg + pocket) applied to content carrying +deg); the net
  // swing was always -pocket + 90 and the card was always visually still.
  //
  // Same picture, and now genuinely still: this is written ONCE per result
  // instead of sixty times a second for as long as the card is on screen.
  const swing = -pocketAngleDeg(winner) + 90;
  el.style.transform = `rotate(${swing}deg)`;
}

/** The rotor-local ANGLE of a pocket, in degrees from the top.
 *
 *  The inset counter-rotates by the rotor angle PLUS this, which swings the
 *  winning pocket up to 12 o'clock. That matters: the numerals face outward, so
 *  a pocket on the far side of the wheel presents its number upside down — and
 *  a rotated "9" reads as a "6" in the one element whose whole job is to remove
 *  doubt about the result. At the top of the wheel an outward-facing numeral is
 *  upright, so anchoring there makes the crop legible for every number. */
function pocketAngleDeg(n: number) {
  return (WHEEL.indexOf(n) * POCKET_ARC * 180) / Math.PI;
}

/** Where the pocket sits once it has been swung to the top — constant. */
const ANCHOR = { x: CX, y: CY - R_REST + 6 };

const p = (r: number, a: number) => `${CX + r * Math.cos(a)},${CY + r * Math.sin(a)}`;

/** Wedge path between two radii for pocket i. */
function wedge(i: number, rIn: number, rOut: number): string {
  const a0 = i * POCKET_ARC - POCKET_ARC / 2 - Math.PI / 2;
  const a1 = a0 + POCKET_ARC;
  return `M ${p(rIn, a0)} L ${p(rOut, a0)} A ${rOut} ${rOut} 0 0 1 ${p(rOut, a1)} L ${p(rIn, a1)} A ${rIn} ${rIn} 0 0 0 ${p(rIn, a0)} Z`;
}

/** Radial fret line at pocket boundary i. */
function fret(i: number, r0: number, r1: number) {
  const a = i * POCKET_ARC - POCKET_ARC / 2 - Math.PI / 2;
  return {
    x1: CX + r0 * Math.cos(a), y1: CY + r0 * Math.sin(a),
    x2: CX + r1 * Math.cos(a), y2: CY + r1 * Math.sin(a),
  };
}

const FILL: Record<string, string> = {
  red: 'url(#rlt-red)', black: 'url(#rlt-black)', green: 'url(#rlt-green)',
};

export default function Wheel({
  spin, startedAt, idleFrom, size = '100%', winner, revealed, ballVisible = false,
}: {
  spin: Spin | null;
  startedAt: number | null;   // performance.now() when the ball launched
  idleFrom: number;           // performance.now() baseline for the resting rotor
  size?: number | string;
  winner: number | null;
  revealed: boolean;
  /** No ball on the wheel until the player presses SPIN. A ball resting in a
   *  pocket while you are still choosing your bets reads as a result that has
   *  already happened — it quietly says the round is over before it has begun. */
  ballVisible?: boolean;
}) {
  const rotorRef = useRef<SVGGElement | null>(null);
  /** the rotating SVG ROOT — the element that actually gets a layer */
  const rotorSvgRef = useRef<SVGSVGElement | null>(null);
  const ballRef = useRef<SVGGElement | null>(null);
  const shadowRef = useRef<SVGEllipseElement | null>(null);
  const glowRef = useRef<SVGCircleElement | null>(null);

  useEffect(() => {
    let raf = 0;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const frame = () => {
      const now = performance.now();
      // Angles are ALWAYS derived from elapsed time, never accumulated — so a
      // 120Hz screen, a slow frame, or a throttled background tab can never
      // desync the ball from its pocket.
      const tRotor = (now - idleFrom) / 1000;
      const rotorA0 = spin ? spin.rotorA0 : 0;
      const rotA = spin && startedAt != null
        ? rotorAngle((now - startedAt) / 1000, rotorA0)
        : idleRotorAngle(tRotor);      // breathes, never winds down to a stop

      const deg = (rotA * 180) / Math.PI;
      if (rotorSvgRef.current) {
        // A CSS TRANSFORM, NOT THE SVG `transform` ATTRIBUTE — and this one line
        // is the single biggest thing in the game's power budget.
        //
        // The rotor holds 224 nodes: 37 outer wedges, 37 inner, 37 grooves, 74
        // frets, 37 numerals and the rings. Writing the SVG attribute marks that
        // whole subtree dirty, so the browser RE-RASTERISES all 224 of them
        // every frame — 60 times a second, forever, because the wheel idles
        // rather than stopping. That is a full repaint of a 355 px disc at
        // device-pixel-ratio 3 (about 1065 x 1065 real pixels) sixty times a
        // second, on the CPU, for the entire time the game is open. It is why
        // the phone gets hot while nothing is even happening.
        //
        // A CSS transform on a `will-change: transform` element is composited:
        // the artwork is rasterised ONCE into a texture and the GPU re-composites
        // that same texture at a new angle each frame. The pixels are identical —
        // it is the same rotation of the same art — but the per-frame cost drops
        // from "repaint 224 vector nodes" to "draw one quad".
        rotorSvgRef.current.style.transform = `rotate(${deg}deg)`;
      }

      // The result inset's camera, written from THIS loop so the game never
      // runs a second animation writer. Counter-rotating by the rotor angle
      // pins the winning pocket AND the ball (which engine.ts parents to the
      // rotor after it seats) motionless at the crop centre, while the bowl
      // visibly turns behind them — which is exactly the reference framing.
      //
      // Never animate the inset's viewBox to do this: a viewBox change forces a
      // full re-rasterisation of that <svg> every frame, worst on Safari.
      wheelCam.deg = deg;
      // The inset is deliberately NOT written here any more. It used to be
      // re-aimed every frame to cancel out the rotor angle; now that the <use>d
      // group no longer carries that angle, the crop is a constant and one write
      // at mount is the whole job. Removing this also removes a second per-frame
      // re-raster of the wheel artwork whenever the result card is on screen.

      if (ballRef.current && ballVisible) {
        let ang: number, rad: number;
        if (spin && startedAt != null) {
          const t = (now - startedAt) / 1000;
          const x = Math.min(1, t / spin.T);
          ang = ballAngle(spin, t);
          rad = ballRadius(x, R_BALL, spin.wobbleSeed);
        } else {
          ang = -tRotor * 0.9;            // gentle idle drift on the track
          rad = R_TRACK;
        }
        // TRANSLATE ONLY — never rotate this group. Rotating it drags the
        // specular highlight around the wheel with the ball, and a light that
        // orbits with the object it lights is the single most obvious "this is
        // fake" tell in the whole animation.
        ballRef.current.setAttribute('transform',
          `translate(${CX + rad * Math.sin(ang)} ${CY - rad * Math.cos(ang)})`);

        // Contact shadow tightens and darkens as the ball drops off the track
        // into the pockets — height, expressed as artwork.
        if (shadowRef.current) {
          const h = Math.max(0, Math.min(1, (rad - R_REST) / (R_TRACK - R_REST)));
          shadowRef.current.setAttribute('ry', String(1.6 + 2.2 * (1 - h)));
          shadowRef.current.setAttribute('opacity', String(0.18 + 0.30 * (1 - h)));
        }
      }

      if (glowRef.current && spin && startedAt != null) {
        const t = (now - startedAt) / 1000;
        glowRef.current.setAttribute('opacity', t >= spin.T ? '0.9' : '0');
      }

      if (!reduce) raf = requestAnimationFrame(frame);
    };
    frame();
    // A hidden tab pauses rAF; recompute immediately on return so the wheel
    // snaps to its correct time-derived state instead of resuming stale.
    //
    // CANCEL FIRST. frame() ends by scheduling itself, so calling it while the
    // existing chain is still pending started a SECOND chain — and every
    // subsequent tab switch doubled it again. Two chains, then four, then eight,
    // all writing the same transforms, and `raf` only ever held the last handle
    // so cleanup could not stop them. A session with a few app switches was
    // running the wheel loop several times over, which is exactly the
    // "gets hotter the longer you play" the owner described.
    const onVis = () => {
      if (document.hidden) return;
      cancelAnimationFrame(raf);
      frame();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelAnimationFrame(raf); document.removeEventListener('visibilitychange', onVis); };
  }, [spin, startedAt, idleFrom, revealed, ballVisible]);

  return (
    /* ── THREE STACKED SVG ROOTS, NOT ONE ──────────────────────────────────
       This is the change that actually stops the phone getting hot, and it is
       here rather than on the rotor group because of one engine detail:

         `will-change: transform` on an SVG element that is NOT an svg root does
         not give it a compositor layer. Blink and WebKit paint inner SVG content
         through LayoutSVG* objects into the enclosing root's layer; only the
         outermost <svg> is a replaced element that can be promoted.

       So rotating the rotor <g> — by attribute or by CSS, with will-change or
       without — re-rasterises all ~245 of its vector nodes every frame, forever,
       because the wheel idles rather than stopping. About half a megapixel of
       path-and-gradient filling, sixty times a second, for as long as the game
       is open.

       Split across three roots, the rotating one IS a root, so it promotes: its
       artwork rasterises once into a texture and the GPU re-composites that
       texture at a new angle each frame.

       PAINT ORDER IS PRESERVED EXACTLY — below the rotor, the rotor, above the
       rotor — which is the whole reason this is three roots and not two. The
       ball is in the top root as well, so its per-frame move no longer dirties
       the rotor's raster either.

       The gradients stay defined once, in the first root. `url(#rlt-brass)` and
       friends resolve document-wide, and all three roots share one viewBox, so
       userSpaceOnUse coordinates are identical in each. */
    <div className="rlt-wheel-stack"
         style={{ width: size, height: size }}>
    {/* Size is CSS, not width/height attributes, so the layout box always equals
        what is painted — a transform:scale() leaves the original box behind and
        gets clipped. */}
    <svg viewBox={`0 0 ${VB} ${VB}`} className="block select-none rlt-wheel-svg"
         aria-label="Roulette wheel">
      <defs>
        <radialGradient id="rlt-cabinet" gradientUnits="userSpaceOnUse"
                        cx={CX} cy={CY} r={240} fx={LIGHT_X} fy={LIGHT_Y}>
          <stop offset="0%" stopColor="#7c4a2c" /><stop offset="55%" stopColor="#5c3421" />
          <stop offset="100%" stopColor="#331c11" />
        </radialGradient>
        {/* The bowl IS the ball track. One wood gradient, lit once. */}
        {/* WOOD RIM. Re-measured as the median over 720 rays of the band
            r/R_OUTER 0.906..0.991: (78,37,17) = #4E2511. The old stops evaluated to
            about (58,28,12) out there — 30% too dark — and the earlier "median
            #552a13" was taken over a narrower, shadier band. It also gets BRIGHTER
            at the very edge (0.966 -> (76,35,14), 0.991 -> (81,51,33)), never
            darker, which is why the extra black hoop below had to go. */}
        <radialGradient id="rlt-bowl" gradientUnits="userSpaceOnUse"
                        cx={CX} cy={CY} r={R_OUTER} fx={LIGHT_X} fy={LIGHT_Y}>
          <stop offset="0%" stopColor="#7e4423" /><stop offset="55%" stopColor="#6a3418" />
          <stop offset="100%" stopColor="#4a2210" />
        </radialGradient>
        {/* THE BALL TRACK. This gradient did not exist — the track ring
            referenced url(#rlt-track) and painted nothing, which is why the
            track read as bare wood. Sampled median #401b09, near-black on the
            shadow side: markedly darker than the rim. */}
        {/* A VERTICAL SWEEP, not a radial. MEASURED over the band r/R_OUTER
            0.735..0.850: quadrant medians top #361505 (L 27), right #3b1706 (29),
            left #411b09 (32), BOTTOM #72503b (L 95) — and the 15-degree bins climb
            monotonically 26.7 at 0deg -> 51.4 at 135 -> 95.1 at 180 -> 45.4 at 240
            -> 26.8 at 345. The track is 3.5x brighter at the bottom than the top.
            A focal radial cannot express that: with F 50 left and 72 above centre
            the offset never leaves 0.81..0.94 anywhere on the ring, so the old
            gradient painted a UNIFORM near-black hoop at L 21-25 all the way round. */}
        <linearGradient id="rlt-track" gradientUnits="userSpaceOnUse"
                        x1={CX} y1={CY - R_RIM_IN} x2={CX} y2={CY + R_RIM_IN}>
          <stop offset="0%" stopColor="#361505" />
          <stop offset="55%" stopColor="#3f1a08" />
          <stop offset="82%" stopColor="#50290f" />
          <stop offset="100%" stopColor="#7a563f" />
        </linearGradient>
        {/* CHROME, AND WHY IT IS BANDED.
            Polished metal does not have a gradient — it has a REFLECTION, and
            what it reflects on a casino table is the bright ceiling above and the
            dark room below, meeting at a horizon. That is why every convincing
            chrome is light/dark/light banding rather than a smooth ramp: the
            bands ARE the room. A smooth silver gradient reads as grey plastic
            every time, which is what the old brass arms looked like. */}
        <linearGradient id="rlt-chrome" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#66727E" />
          <stop offset=".16" stopColor="#F2F7FB" />
          <stop offset=".34" stopColor="#AFBDCA" />
          <stop offset=".50" stopColor="#FFFFFF" />
          <stop offset=".66" stopColor="#75838F" />
          <stop offset=".84" stopColor="#DFE9F1" />
          <stop offset="1" stopColor="#4E5964" />
        </linearGradient>
        <radialGradient id="rlt-chrome-dome" cx=".36" cy=".28" r=".82">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset=".30" stopColor="#DCE6EE" />
          <stop offset=".58" stopColor="#93A1AE" />
          <stop offset=".82" stopColor="#535E69" />
          <stop offset="1" stopColor="#2C343C" />
        </radialGradient>

        {/* THE TWO FACES OF A STUD. Deliberately a wide separation rather than a
            subtle one — at the size these render on a phone, anything less than a
            clear light-side / dark-side split just averages into one flat tone,
            which is exactly what they looked like before. */}
        <linearGradient id="rlt-stud-lit" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFF6D2" />
          <stop offset=".45" stopColor="#F3D479" />
          <stop offset="1" stopColor="#D8AC44" />
        </linearGradient>
        <linearGradient id="rlt-stud-dark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#9C7420" />
          <stop offset=".55" stopColor="#6E4E0E" />
          <stop offset="1" stopColor="#432E05" />
        </linearGradient>
        <radialGradient id="rlt-brass" gradientUnits="userSpaceOnUse"
                        cx={CX} cy={CY} r={200} fx={LIGHT_X} fy={LIGHT_Y}>
          <stop offset="0%" stopColor="#ffeaa8" /><stop offset="45%" stopColor="#c9a55c" />
          <stop offset="100%" stopColor="#8a6018" />
        </radialGradient>
        {/* Scarlet, not crimson — the reference reds are bright and saturated. */}
        {/* Pocket colours, SAMPLED: red #dd2812, black #221b0a — a WARM
            near-black, not neutral grey — and green #07a205. */}
        {/* Pocket colours from a 37-pocket sweep. Red's real variation is
            LEFT-to-RIGHT (#EF2A16 at 351deg, #D82913 at 205deg), not top to
            bottom, and its darkest point never reaches the #c11f0c a previous
            build used. Black is a warm olive-brown with R>G>B on all 18. */}
        <linearGradient id="rlt-red" gradientUnits="userSpaceOnUse" x1={CX - 140} y1={CY} x2={CX + 140} y2={CY}>
          <stop offset="0%" stopColor="#D82913" /><stop offset="100%" stopColor="#EF2A16" />
        </linearGradient>
        <linearGradient id="rlt-black" gradientUnits="userSpaceOnUse" x1={CX - 140} y1={CY} x2={CX + 140} y2={CY}>
          <stop offset="0%" stopColor="#241C08" /><stop offset="100%" stopColor="#291F0D" />
        </linearGradient>
        <linearGradient id="rlt-green" gradientUnits="userSpaceOnUse" x1={CX - 140} y1={CY} x2={CX + 140} y2={CY}>
          <stop offset="0%" stopColor="#039C03" /><stop offset="100%" stopColor="#00B300" />
        </linearGradient>
        {/* ── THE GLOSSY CENTRE WOOD ──────────────────────────────────────
            Called out by the owner specifically: it has to read as POLISHED
            LACQUER over burled walnut, not as a painted disc. Five stacked
            layers do it, all static artwork — a filter here would be re-run
            every single frame, because this sits inside the rotor. */}
        {/* 1. the burl itself, lit from the shared light */}
        <radialGradient id="rlt-burl" gradientUnits="userSpaceOnUse"
                        cx={CX} cy={CY} r={R_CONE} fx={CX - 26} fy={CY - 30}>
          <stop offset="0%" stopColor="#7d5320" /><stop offset="34%" stopColor="#5c2e0c" />
          <stop offset="70%" stopColor="#431904" />
          <stop offset="100%" stopColor="#240d02" />
        </radialGradient>
        {/* 2. the broad sheen the grain smears into as it turns */}
        {/* MOVED to the lower left. A 15-degree x radius grid over the cone puts the
            ONLY bright region at bearings 200-245deg (L 70-81 against 29-41
            everywhere else). Compositing all seven cone layers analytically showed
            the implementation peaking at bearings 345-45 with L 66-75 and bottoming
            out at 150-225 with L 22-25 — the reference's bright wedge was our
            darkest region, exactly inverted. The wedge path further down was already
            in the right place, but with the sheen's centre on the opposite side the
            gradient offset ran 0.90-1.39 inside it, so it deposited alpha <= 0.04:
            it painted nothing at all. */}
        <radialGradient id="rlt-cone-sheen" gradientUnits="userSpaceOnUse"
                        cx={CX - 34} cy={CY + 46} r={R_CONE * 0.85}>
          <stop offset="0%" stopColor="#f0cb96" stopOpacity=".62" />
          <stop offset="34%" stopColor="#d09b5e" stopOpacity=".34" />
          <stop offset="72%" stopColor="#8a5730" stopOpacity=".10" />
          <stop offset="100%" stopColor="#8a5730" stopOpacity="0" />
        </radialGradient>
        {/* 3. the shadowed quarter, opposite the light */}
        {/* SWAPPED. A 10deg x r grid over the cone puts the BRIGHTEST region in
            the 209-250deg wedge — lower-left, strongest at its outer end
            (#784C1D, L 83.8) — and the DARKEST in the ring around the hub on
            the upper-left (#330B00, L 21.7). The previous build had the light
            and the shadow exactly the wrong way round. */}
        {/* and the shadow to the upper RIGHT, opposite it — the reference measures
            L 33-37 where this used to put its darkest point. */}
        <radialGradient id="rlt-cone-shade" gradientUnits="userSpaceOnUse"
                        cx={CX + 26} cy={CY - 30} r={R_CONE * 0.6}>
          <stop offset="0%" stopColor="#160a02" stopOpacity=".62" />
          <stop offset="55%" stopColor="#160a02" stopOpacity=".26" />
          <stop offset="100%" stopColor="#160a02" stopOpacity="0" />
        </radialGradient>
        {/* 4. the rim vignette — a polished dome darkens at its edge */}
        <radialGradient id="rlt-cone-vig" gradientUnits="userSpaceOnUse"
                        cx={CX} cy={CY} r={R_CONE}>
          <stop offset="55%" stopColor="#000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000" stopOpacity=".58" />
        </radialGradient>
        {/* 4b. the lit slope near the hub */}
        <radialGradient id="rlt-cone-crest" gradientUnits="userSpaceOnUse"
                        cx={CX - 52} cy={CY + 60} r={R_CONE * 0.9}>
          <stop offset="0%" stopColor="#ffdca8" stopOpacity=".30" />
          <stop offset="60%" stopColor="#e0b27a" stopOpacity=".08" />
          <stop offset="100%" stopColor="#e0b27a" stopOpacity="0" />
        </radialGradient>
        {/* 5. THE LACQUER. Deliberately NOT inside the rotor: a reflection of
            the room stays put while the wood turns underneath it, and that
            contrast is the whole reason the surface reads as glossy rather
            than painted. */}
        {/* Cut from .34: at full strength this ramp alone held the upper-left above
            L 65, which kept it the brightest part of the cone even after the sheen
            and the shade were swapped. */}
        <linearGradient id="rlt-cone-gloss" gradientUnits="userSpaceOnUse"
                        x1={CX - R_CONE} y1={CY - R_CONE} x2={CX + R_CONE * 0.5} y2={CY + R_CONE}>
          <stop offset="0%" stopColor="#fff" stopOpacity=".14" />
          <stop offset="30%" stopColor="#fff" stopOpacity=".13" />
          <stop offset="56%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="rlt-ball" gradientUnits="userSpaceOnUse" cx={-2.1} cy={-2.4} r={9}>
          <stop offset="0%" stopColor="#ffffff" /><stop offset="38%" stopColor="#eceef2" />
          <stop offset="76%" stopColor="#b9bcc6" /><stop offset="100%" stopColor="#7d818b" />
        </radialGradient>
      </defs>

      {/* NO CABINET RECT HERE.
          It used to draw a rounded square filling the viewBox, which made the
          wheel read as a boxed panel floating on the felt — the owner's exact
          complaint. In the reference the wood runs off BOTH screen edges, so
          you never see it as a discrete object. That surround is now a
          full-bleed CSS panel on .rlt-wheelbay, and this SVG draws only the
          bowl inward. */}

      {/* ── the wooden RIM: a narrow band, 0.862 -> 0.945 ──
           Drawn to R_RIM_OUT, not R_OUTER: the owner asked for a narrower rim,
           and trimming the OUTER edge is the only way to do that without moving
           the ball track (which would darken the whole wheel) or any of the
           measured radii inside it. */}
      <circle cx={CX} cy={CY} r={R_RIM_OUT} fill="url(#rlt-bowl)" />
      {/* ── the DARK BALL TRACK, 0.707 -> 0.862 ── */}
      <circle cx={CX} cy={CY} r={(R_GOLD + R_RIM_IN) / 2} fill="none"
              stroke="url(#rlt-track)" strokeWidth={R_RIM_IN - R_GOLD} />
      {/* the track's specular, ACROSS THE BOTTOM. In SVG's y-down frame the old
          -2.5..-0.9 rad swept 307deg -> 38deg clockwise from 12, i.e. over the top,
          on the side the measurement says is darkest. +1.05..+2.18 rad is
          150deg -> 215deg: the bottom. */}
      <path
        d={`M ${CX + R_TRACK * Math.cos(1.05)} ${CY + R_TRACK * Math.sin(1.05)}
            A ${R_TRACK} ${R_TRACK} 0 0 1 ${CX + R_TRACK * Math.cos(2.18)} ${CY + R_TRACK * Math.sin(2.18)}`}
        fill="none" stroke="rgba(255,255,255,.13)" strokeWidth="10" strokeLinecap="round" />
      {/* THE RIM LIP. Radial medians across 720 rays: 0.855 (49,20,6), 0.863
          (57,22,5), then 0.872 (116,72,51) and 0.880 (113,64,40) before falling back
          to 0.897 (86,41,19) — a BRIGHT caramel lip at r/R_OUTER 0.876, bright the
          whole way round and brightest at the bottom. We were painting a 33% black
          hoop over exactly those radii. */}
      <circle cx={CX} cy={CY} r={R_RIM_IN - 3} fill="none" stroke="rgba(0,0,0,.55)" strokeWidth="4" opacity=".6" />
      <circle cx={CX} cy={CY} r={0.876 * R_OUTER} fill="none" stroke="#815341" strokeWidth="4.2" opacity=".9" />

      {/* eight brass diamond fittings, set INTO the wood inside the ball's path
          so the ball strikes them on the way down */}
      {/* Measured at 0.3 / 44.7 / 89.6 / 135.0 / 179.7 / 225.2 / 270.4 / 315.1
          degrees — one at 12 o'clock and one at 3 o'clock, so NO 22.5deg offset.
          And they come in two families: the four on the axes lie TANGENTIALLY,
          the four on the diagonals RADIALLY (principal axis vs radial measured
          87.2/0.4/89.7/4.0/88.0/3.6/86.2/1.6). The SHAPE is a four-faceted
          lozenge — see the long note inside. */}
      {Array.from({ length: 8 }, (_, i) => {
        const a = (i * TAU) / 8;
        const x = CX + R_FITTING * Math.sin(a), y = CY - R_FITTING * Math.cos(a);
        const tangential = i % 2 === 0;

        /* ═══ THE STUDS, REBUILT — and why the previous two attempts failed ═══
           These are the deflectors ("diamonds") a real wheel has set into the
           wood, and they have to read as SOLID METAL SITTING PROUD OF THE
           SURFACE. Twice they did not, for two different reasons:

           1. The shape was a LEAF — two curves meeting at pointed ends — at an
              8:1 aspect. Measured on a live 355px wheel that is 23.5 x 2.8 px.
              Nothing that thin can read as an object; it reads as a scratch.
              Owner: "they are just looking like lines, the middle section is fat
              and two ends are pointier."
           2. My first 3D pass then split that sliver ALONG its long axis, so each
              "facet" was 1.4 px wide. The two-tone brass was in the code and
              invisible on the glass. Adding shading to a shape too thin to hold
              it is not a fix.

           So: a proper LOZENGE at 2.6:1, and FOUR facets instead of two. Four is
           what makes it a stud rather than a shape with a gradient on it — a
           pyramid has four planes meeting at an apex, and each one takes a
           different amount of light, so the eye gets the same cue it gets from a
           real one. At 2.6:1 each facet is ~5 px across and the tones are
           genuinely visible.

              A ──── the four corners of the lozenge, and O the raised apex
             /|\\        A(-L,0)  B(0,-W)  C(L,0)  D(0,W)  O(0,0)
            / | \\       facets:  A-B-O   B-C-O   C-D-O   D-A-O
           D--O--B
            \\ | /       lit from the front and upper left, so A-B is brightest
             \\|/        and C-D is darkest — a hard, obvious split.
              C
           The lozenge is built with its long axis on X and the <g> rotates it
           into place, so the tangential and radial families are the same code
           with L and W swapped — which is also what keeps every stud's lighting
           consistent relative to itself. */
        const L = (tangential ? 0.132 : 0.112) * R_OUTER / 2;
        const W = 0.052 * R_OUTER / 2;
        // long axis on X for the tangential family, on Y for the radial one
        const [ax, ay] = tangential ? [L, 0] : [0, L];
        const [bx, by] = tangential ? [0, W] : [W, 0];
        const P = (px: number, py: number) => `${px.toFixed(2)} ${py.toFixed(2)}`;
        const facet = (p1: [number, number], p2: [number, number]) =>
          `M ${P(p1[0], p1[1])} L ${P(p2[0], p2[1])} L 0 0 Z`;
        const A: [number, number] = [-ax, -ay];
        const B: [number, number] = [-bx, -by];
        const C: [number, number] = [ax, ay];
        const D: [number, number] = [bx, by];
        const outline = `M ${P(A[0], A[1])} L ${P(B[0], B[1])} L ${P(C[0], C[1])} L ${P(D[0], D[1])} Z`;

        return (
          <g key={`f${i}`} transform={`translate(${x} ${y}) rotate(${(a * 180) / Math.PI})`}>
            {/* THE SOCKET. A stud is set INTO the wood, so there is a shadow all
                round where it meets the surface — and with the key light in front
                of the table rather than above it, that shadow is even rather than
                thrown to one side. This is what stops the stud floating. */}
            {/* Socket, thinned on the owner's call — it was reading as a heavy
                brown outline round each stud rather than as the shadow of one set
                into wood. Tighter rings, lower opacity: the stud still sits IN
                the surface, but the brass is what you see. */}
            <path d={outline} transform="scale(1.30)" fill="rgba(0,0,0,.30)" />
            <path d={outline} transform="scale(1.09)" fill="rgba(30,16,5,.62)" />

            {/* the four planes, brightest at the top-left, darkest opposite */}
            <path d={facet(A, B)} fill="#FFF2C6" />
            <path d={facet(B, C)} fill="#E8BE5C" />
            <path d={facet(C, D)} fill="#7A5511" />
            <path d={facet(D, A)} fill="#B08526" />

            {/* the four ridges running down from the apex. Without these the
                facets meet in a colour change alone, which the eye reads as a
                gradient; a hairline of true highlight at the crease is what says
                "edge". */}
            <path d={`M ${P(A[0], A[1])} L 0 0 M ${P(B[0], B[1])} L 0 0`}
                  stroke="rgba(255,250,224,.75)" strokeWidth=".45" fill="none" />
            <path d={`M ${P(C[0], C[1])} L 0 0 M ${P(D[0], D[1])} L 0 0`}
                  stroke="rgba(60,40,4,.65)" strokeWidth=".45" fill="none" />

            {/* apex spark + the rim that separates brass from wood */}
            <circle r={W * 0.20} cx={-ax * 0.10 - bx * 0.10} cy={-ay * 0.10 - by * 0.10}
                    fill="#FFFDF0" opacity=".9" />
            <path d={outline} fill="none" stroke="#5A4109" strokeWidth=".3" opacity=".75" />
          </g>
        );
      })}

      {/* the gold ring at the band's outer edge (measured 0.707) */}
      <circle cx={CX} cy={CY} r={R_GOLD} fill="none" stroke="rgba(0,0,0,.45)" strokeWidth="7" />
      <circle cx={CX} cy={CY} r={R_GOLD} fill="none" stroke="url(#rlt-brass)" strokeWidth="4.5" />
      <circle cx={CX} cy={CY} r={R_GOLD - 1.6} fill="none" stroke="rgba(255,238,196,.55)" strokeWidth="1" />

    </svg>

    {/* ROOT 2 — THE ONLY THING THAT MOVES, and therefore the only thing that
        needs a layer. The rotation is written to THIS element's style by the rAF
        loop; see the note at the top of the return. */}
    <svg ref={rotorSvgRef} viewBox={`0 0 ${VB} ${VB}`}
         className="block select-none rlt-wheel-svg rlt-wheel-rotor"
         aria-hidden>
      {/* ── the wheel proper. id is consumed by the result inset, which shows a
             magnified crop of this same live group through <use>. ── */}
      <g id="rlt-wheel-root">
        {/* ROTOR — the ONLY rotating group, and the ball locks to it once it
            settles. NO FILTERS IN HERE. */}
        <g ref={rotorRef}>
          <circle cx={CX} cy={CY} r={R_BAND_OUT} fill="#141210" />

          {/* ── the OUTER pocket ring: colour + the numeral printed on it ── */}
          {WHEEL.map((n, i) => (
            <path key={n} d={wedge(i, R_BAND_IN, R_BAND_OUT)} fill={FILL[colourOf(n)]} />
          ))}

          {/* ── the INNER pocket ring — the SAME 37 pockets, slightly darker.
                 A 37-ray polar sample gives an identical colour assignment at
                 r=112 and r=145, with the zero centred at 215.05deg on the
                 outer ring and 215.25deg on the inner. ── */}
          {WHEEL.map((n, i) => (
            <path key={`i${n}`} d={wedge(i, R_RING2_IN, R_RING2_OUT)}
                  fill={FILL[colourOf(n)]} opacity=".93" />
          ))}

          {/* The groove between them is each pocket's OWN colour at ~70% L —
              a darkening, not a flat dark band. */}
          {WHEEL.map((n, i) => (
            <path key={`g${n}`} d={wedge(i, R_GROOVE - R_GROOVE_W, R_GROOVE + R_GROOVE_W)}
                  fill="#000" opacity=".30" />
          ))}

          {/* the thin gold ring inboard of the inner pocket ring */}
          <circle cx={CX} cy={CY} r={R_GOLD_IN} fill="none"
                  stroke="url(#rlt-brass)" strokeWidth={R_GOLD_IN_W} />

          {/* BRASS FRETS ON THE INNER RING ONLY — they stop at R_RING2_OUT, not
              R_BAND_OUT. Fitting the 37 pocket boundaries on the close-up (offset
              3.96deg, step 9.7297deg, rms 0.30deg) and counting brass (R>140, G>80)
              among the brightest pixel within +/-0.58deg of each boundary gives
              29/37 at r/R_OUTER 0.449 through 29/37 at 0.534 — then 1/37 at 0.545
              and 0/37 at 0.556, 0.577, 0.598, 0.641 and 0.679. Boundary median RGB
              is (182,115,87) at r=115 (a strong green lift = brass) but (216,54,32)
              at r=150: pure pocket red, no lift at all. A single ray across one
              boundary at r=157 is a monotone ramp with no bright pixel anywhere.
              So the OUTER numbered ring's pockets simply abut — no brass divider and
              no dark one either. We were running 37 brass lines plus 37 dark twins
              the full height of it, straight past the numerals.
              Each fret keeps its shadow-side twin so the divider reads as a raised
              metal blade rather than a drawn line. */}
          {Array.from({ length: POCKETS }, (_, i) => {
            const s = fret(i, R_RING2_IN, R_RING2_OUT);
            return <line key={`s${i}`} x1={s.x1 + 0.9} y1={s.y1 + 0.9} x2={s.x2 + 0.9} y2={s.y2 + 0.9}
                         stroke="#00000077" strokeWidth=".9" />;
          })}
          {Array.from({ length: POCKETS }, (_, i) => {
            const s = fret(i, R_RING2_IN, R_RING2_OUT);
            return <line key={`b${i}`} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
                         stroke="url(#rlt-brass)" strokeWidth="1.5" />;
          })}

          {WHEEL.map((n, i) => {
            const a = i * POCKET_ARC;
            const x = CX + R_NUM * Math.sin(a), y = CY - R_NUM * Math.cos(a);
            return (
              <text key={`t${n}`} x={x} y={y}
                    transform={`rotate(${(a * 180) / Math.PI + NUMERAL_FACING} ${x} ${y})`}
                    textAnchor="middle" dy="0.35em"
                    // Ink height measured 15px = 0.065 R; the old 13 was 26%
                    // too small. Warm cream, and NO black outline is visible at
                    // any zoom in the reference, so the stroke is gone.
                    fontSize="17.5" fontWeight="800" fill="#FAF3E0" letterSpacing="-0.3"
                    // A pinned stack, not system-ui: system-ui resolves to Roboto
                    // on Android and SF on iOS, whose digits have different
                    // advance widths — the numerals would fit their pockets on
                    // one platform and crowd them on the other.
                    style={{ fontFamily: '"Arial Narrow", "Helvetica Neue", Arial, sans-serif' }}>
                {n}
              </text>
            );
          })}

          {/* NO AO SEAM HERE. It used to be a 6-wide 30%-black stroke at
              r = R_CONE + 2 (vb 81..87) emitted AFTER the inner gold ring
              (vb 82.6..86.9), so its only visible effect was to black the brass
              ring out. The reference has no dip on either side of that ring: the
              black-pocket luminance runs 30.6 at r/R_OUTER 0.406, 48.9 at 0.419,
              85.0-86.2 across 0.423-0.427, 56.2 at 0.432 — a clean peak, brass
              abutting the cone directly. Relocating it inboard would not help
              either: the burl circle at r = R_CONE is drawn immediately below and
              would paint straight over it, so it would be dead markup. */}

          {/* ── the glossy centre wood ── */}
          <circle cx={CX} cy={CY} r={R_CONE} fill="url(#rlt-burl)" />
          <circle cx={CX} cy={CY} r={R_CONE} fill="url(#rlt-cone-sheen)" />
          {/* one wide soft wedge, narrow at the hub and opening outward — what
              a polished cone's reflection actually looks like when it spins,
              for one path instead of a motion-blur filter */}
          {/* 41deg wide at 209-250deg — lower-left, outer-weighted. It was a
              114deg sweep across the TOP. */}
          <path d={`M ${CX},${CY} L ${p(R_CONE, 1.92)} A ${R_CONE} ${R_CONE} 0 0 1 ${p(R_CONE, 2.63)} Z`}
                fill="url(#rlt-cone-sheen)" opacity=".7" />
          <circle cx={CX} cy={CY} r={R_CONE} fill="url(#rlt-cone-shade)" />
          {/* eight crisp seams, each with a light side so it reads as a joint
              between two polished segments rather than a drawn line */}
          {Array.from({ length: 8 }, (_, i) => {
            const a = (i * TAU) / 8 - Math.PI / 2;
            const ex = CX + R_CONE * Math.cos(a), ey = CY + R_CONE * Math.sin(a);
            const nx = Math.cos(a + Math.PI / 2) * 0.9, ny = Math.sin(a + Math.PI / 2) * 0.9;
            return (
              <g key={`sp${i}`}>
                <line x1={CX + nx} y1={CY + ny} x2={ex + nx} y2={ey + ny}
                      stroke="rgba(255,226,184,.16)" strokeWidth="1.1" />
                <line x1={CX} y1={CY} x2={ex} y2={ey}
                      stroke="rgba(8,3,0,.92)" strokeWidth="3" />
              </g>
            );
          })}
          <circle cx={CX} cy={CY} r={R_CONE} fill="url(#rlt-cone-vig)" />
          {/* the cone RISES to the hub, so the slope facing the light catches a
              bright crescent near the centre. Without it the surface reads as a
              flat disc no matter how glossy the rest of the shading is. */}
          <circle cx={CX} cy={CY} r={R_CONE * 0.52} fill="url(#rlt-cone-crest)" />

          {/* winning pocket marker — only after the reveal */}
          {revealed && winner != null && (() => {
            const i = WHEEL.indexOf(winner);
            const a = i * POCKET_ARC;
            return <circle ref={glowRef} cx={CX + R_REST * Math.sin(a)} cy={CY - R_REST * Math.cos(a)}
                           r="15" fill="none" stroke="#ffe103" strokeWidth="2.5" opacity="0.9" />;
          })()}

          {/* brass hub. It sits ON the lacquer, so it casts a soft ring of
              shadow onto it — without that it looks printed rather than
              mounted. */}
          <circle cx={CX} cy={CY} r={R_HUB + 5} fill="rgba(0,0,0,.34)" />
          {/* ═══ THE SPINNER — the four arms the croupier turns the wheel by ═══
              Every real wheel has them and ours had four flat brass triangles
              standing in for them: no taper, no thickness, no reflection, so they
              read as painted marks rather than as a handle you could grip.

              Rebuilt as CHROME, because that is what they are on the wheel in the
              owner's photograph, and chrome is what makes them look machined
              rather than drawn. Each arm is a tapered blade — wide at the boss,
              narrow at the tip, rounded off — filled with the banded chrome above
              so the reflection runs across its width. Then a bright edge down one
              side and a dark one down the other, which is what gives a flat SVG
              shape a thickness the eye will accept.

              Kept SMALL on the owner's instruction — "not big ones but smaller
              ones, beautiful ones". They reach 1.55x the boss radius: enough to
              read as a handle, not enough to crowd the cone.

              They live in the ROTOR, so they turn with the wheel — a spinner that
              stayed still while the wheel moved would be the one detail that gave
              the whole thing away. */}
          {[0, 1, 2, 3].map((i) => (
            <g key={`x${i}`} transform={`rotate(${i * 90} ${CX} ${CY})`}>
              {/* the blade's own shadow on the cone, tight and even — frontal key */}
              {/* Reach 40 (1.9x the boss) rather than 30 — owner's call, "slightly
                  longer". Still short of the cone's edge, so they read as a
                  handle on the hub rather than as spokes crossing the wood. */}
              <path d={`M ${CX - 4.6} ${CY + 1} L ${CX - 2.1} ${CY - 40}
                        Q ${CX} ${CY - 45} ${CX + 2.1} ${CY - 40}
                        L ${CX + 4.6} ${CY + 1} Z`}
                    fill="rgba(0,0,0,.34)" transform="translate(.7 1.1)" />
              <path d={`M ${CX - 4.4} ${CY} L ${CX - 2.0} ${CY - 40}
                        Q ${CX} ${CY - 44.5} ${CX + 2.0} ${CY - 40}
                        L ${CX + 4.4} ${CY} Z`}
                    fill="url(#rlt-chrome)" />
              {/* lit edge / shaded edge — the thickness */}
              <path d={`M ${CX - 4.4} ${CY} L ${CX - 2.0} ${CY - 40}`}
                    stroke="rgba(255,255,255,.92)" strokeWidth=".8" fill="none"
                    strokeLinecap="round" />
              <path d={`M ${CX + 4.4} ${CY} L ${CX + 2.0} ${CY - 40}`}
                    stroke="rgba(30,38,46,.75)" strokeWidth=".8" fill="none"
                    strokeLinecap="round" />
              {/* the highlight that says "polished", not "grey" */}
              <path d={`M ${CX - 1.1} ${CY - 4} L ${CX - 0.5} ${CY - 36}`}
                    stroke="#FFFFFF" strokeWidth="1.1" fill="none" opacity=".8"
                    strokeLinecap="round" />
            </g>
          ))}

          {/* the boss: a brass collar with a chrome dome sitting in it, which is
              exactly the stack in the photograph — warm metal ring, cold metal
              turret. */}
          <circle cx={CX} cy={CY} r={R_HUB} fill="url(#rlt-brass)" stroke="#6d4f0c" strokeWidth="1.2" />
          <circle cx={CX} cy={CY} r={R_HUB - 4} fill="none" stroke="rgba(255,236,190,.45)" strokeWidth="1" />
          <circle cx={CX} cy={CY} r={R_HUB - 5.5} fill="rgba(0,0,0,.35)" />
          <circle cx={CX} cy={CY} r={R_HUB - 6.5} fill="url(#rlt-chrome-dome)" />
          {/* the specular pip — one small hard white spot is what reads as a
              curved polished surface; a soft one reads as fog. */}
          <ellipse cx={CX - 3.4} cy={CY - 4.2} rx="2.4" ry="1.7" fill="#FFFFFF" opacity=".95" />
          <circle cx={CX} cy={CY} r={R_HUB - 6.5} fill="none"
                  stroke="rgba(255,255,255,.45)" strokeWidth=".6" />
          <circle cx={CX - 2} cy={CY - 2.6} r="3" fill="rgba(255,240,200,.5)" />
        </g>
      </g>

    </svg>

    {/* ROOT 3 — everything that paints ABOVE the rotor. Static lacquer plus the
        ball, which moves on its own and is better off not sharing a raster with
        245 pocket nodes. */}
    <svg viewBox={`0 0 ${VB} ${VB}`} className="block select-none rlt-wheel-svg"
         aria-hidden>
      {/* THE LACQUER HIGHLIGHT — deliberately outside <g ref={rotorRef}>.
          A reflection of the room does not travel with the wood it is
          reflecting: holding this still while the cone spins underneath is
          precisely what makes the surface read as polished. Inside the rotor it
          would orbit with the grain and the whole thing would look painted. */}
      <circle cx={CX} cy={CY} r={R_CONE} fill="url(#rlt-cone-gloss)" pointerEvents="none" />

      {/* The ball rides ABOVE the rotor so it is never hidden by a pocket, and
          does not exist at all until SPIN is pressed. Four elements, no filters:
          contact shadow, body, specular, rim light. */}
      {ballVisible && (
        <g ref={ballRef}>
          <ellipse ref={shadowRef} cx="0.8" cy="4.6" rx="6.2" ry="2.4" fill="#000" opacity=".3" />
          <circle r="6.5" fill="url(#rlt-ball)" />
          <ellipse cx="-2.1" cy="-2.4" rx="2.2" ry="1.7" fill="#fff" opacity=".92" />
          <path d="M 4.6 2.6 A 6.5 6.5 0 0 1 -1.4 6.3" fill="none"
                stroke="rgba(255,255,255,.28)" strokeWidth="1.3" strokeLinecap="round" />
        </g>
      )}
    </svg>
    </div>
  );
}

/**
 * THE RESULT INSET — a magnified live crop of the winning pocket, plus the
 * number spelled out large beside it.
 *
 * It exists because the wheel alone is not a readable answer: the reference
 * slides this in from the left edge as the camera pans back down, so the player
 * reads the result without hunting for a ball among 37 pockets.
 */
export function ResultInset({ winner }: { winner: number }) {
  const camRef = useRef<SVGGElement | null>(null);

  useEffect(() => {
    wheelCam.inset = camRef.current;
    wheelCam.winner = winner;
    // Paint correctly NOW, not on the next animation frame. The wheel's rAF
    // does not re-run when this mounts (winner is not in its dependency list),
    // so without this the crop would be wrong until the next frame — and wrong
    // indefinitely wherever rAF is throttled.
    if (camRef.current) writeInsetCam(camRef.current, winner);
    return () => { wheelCam.inset = null; wheelCam.winner = null; };
  }, [winner]);

  return (
    <div className="rlt-inset" role="status" aria-label={`Result ${winner}`}>
      <div className="rlt-inset-wheel">
        <svg viewBox={`0 0 ${INSET_W} ${INSET_H}`} aria-hidden>
          <g ref={camRef}
             style={{ transformBox: 'view-box',
                      transformOrigin: `${CX}px ${CY}px`,
                      willChange: 'transform' }}>
            {/* Both spellings: xlink:href for older WebKit, href for everything
                else. The source stays mounted and is never display:none — a
                <use> of a hidden element renders nothing. */}
            <use href="#rlt-wheel-root" xlinkHref="#rlt-wheel-root" />
          </g>
          {/* THE BALL, SEATED. The <use> above only carries the rotor group —
              the live ball lives in the wheel's TOP svg root, so the card was
              showing the winner's ring with nothing in it. The cam swings the
              winning pocket to exactly 3 o'clock, so the seated ball is a
              CONSTANT position here: (CX + R_REST, CY), drawn OUTSIDE the
              rotating group with the same gradient the real ball wears. */}
          <g transform={`translate(${CX + R_REST} ${CY})`}>
            <ellipse cx="0.7" cy="3.8" rx="5.4" ry="2.1" fill="#000" opacity=".3" />
            <circle r="5.8" fill="url(#rlt-ball)" />
            <ellipse cx="-1.9" cy="-2.1" rx="2" ry="1.5" fill="#fff" opacity=".92" />
          </g>
        </svg>
      </div>
      {/* ONE brown RING for every result — ref5's black 10 and ref6's black 13
          sample the same #723f2c..#7a3d28, so the ring does not track the colour.
          The DISC does: owner's call, the player has to see at a glance whether a
          red or a black number came up. Both reference frames happen to show black
          results, so red and green are built from the board's own measured disc
          fills rather than guessed at. */}
      <div className={`rlt-inset-plate rlt-inset-plate--${colourOf(winner)}`}>
        <span className="rlt-inset-n">{winner}</span>
      </div>
    </div>
  );
}
